import React, { FC, Fragment, useEffect, useMemo, useRef } from 'react';
import { useAsync, useDebounce } from 'react-use';
import { useSnackbar } from 'notistack';
import { useEditAccountMutation, useEditSiteDesignMutation } from '../../../generated/types';

import { useUpload } from '../../../common/hooks/use-upload';
import Dialog from '../../../components/dialog/Dialog';
import { useEditor } from '@mnz/builder';
import { useQueryParam } from '../../../common/hooks/use-query-param';
import { useGeoLocationLazyQuery } from '../../../generated/types';
import emptyToNull from '../../../shared/utils/object/empty-to-null';
import { MODAL } from '../modals/config';

interface ElementSettingDialogProps {
    setModal: (modal: null | string) => void;
}

const ElementSettingDialog: FC<ElementSettingDialogProps> = ({ setModal }) => {
    const abortController = useRef<AbortController>();
    const accountAbortController = useRef<AbortController>();
    const [updateAccount] = useEditAccountMutation();
    const [saveDraftDesign] = useEditSiteDesignMutation();

    const { uploadImage } = useUpload();

    const { enqueueSnackbar } = useSnackbar();
    const [nodeId, setNodeId] = useQueryParam<string | null>('nodeId');
    const {
        actions: { selectNode, setAccount },
        query: { getNodes },
        currentNodeId,
        account,
        designSettings,
        elements,
    } = useEditor((state, query) => {
        const currentNodeId = query.getEvent('selected').last() || nodeId;
        return {
            currentNodeId,
            isEnabled: state.options.enabled,
            account: query.getAccountInfo(), //TODO remove this if want to reduce request
            designSettings: query.getDesignSettings(),
            elements: query.serialize(),
        };
    });

    const nodes = getNodes();
    const settingList = useMemo(() => {
        return Object.keys(nodes)
            .map((nodeId) => {
                return {
                    id: nodeId,
                    name: nodes[nodeId].data.displayName || nodes[nodeId].data.name,
                    settings: nodes[nodeId].related && nodes[nodeId].related.settings,
                    configs: nodes[nodeId].configs,
                };
            })
            .filter((s) => s.settings);
    }, [nodes]);

    useEffect(() => {
        if (typeof nodeId === 'string') {
            selectNode(nodeId);
        } else {
            selectNode();
        }
    }, [nodeId]);

    useEffect(() => {
        setNodeId(currentNodeId, true);
    }, [currentNodeId]);

    useDebounce(
        async () => {
            if (accountAbortController.current) {
                accountAbortController.current.abort();
            }
            accountAbortController.current = new window.AbortController();

            await updateAccount({
                variables: {
                    input: {
                        patch: emptyToNull({
                            name: account.name,
                            logoList: account.logoList,
                            about: account.about,
                            phone: account.phone,
                            email: account.email,
                            website: account.website,
                            bookingUrl: account.bookingUrl,
                            address: account.address,
                            customAddress: account.customAddress,
                            note: account.note,
                            longitude: account.longitude,
                            latitude: account.latitude,
                            city: account.city,
                            state: account.state,
                            country: account.country,
                            countryCode: account.countryCode,
                            timezone: account.timezone,
                            postcode: account.postcode,
                            openingHours: account.openingHours,
                            socialLinks: account.socialLinks,
                            orderLinks: account.orderLinks,
                        }),
                    },
                },
                context: { fetchOptions: { signal: accountAbortController.current.signal } },
                onError: (e: any) => {
                    console.log(e);
                    enqueueSnackbar('Cannot update account.', { variant: 'error' });
                },
                onCompleted(data: any) {
                    const updatedAccount = data.editAccount?.account;
                    if (updatedAccount) {
                        const newAccount = {
                            ...account,
                            name: updatedAccount.name,
                            socialLinks: updatedAccount.socialLinks,
                            orderLinks: updatedAccount.orderLinks,
                        };
                        setAccount(newAccount);
                    }
                },
            });
        },
        1000,
        [account],
    );

    useDebounce(
        async () => {
            if (designSettings && elements !== '{}') {
                if (abortController.current) {
                    abortController.current.abort();
                }
                abortController.current = new window.AbortController();
                await saveDraftDesign({
                    variables: {
                        input: {
                            patch: {
                                designSettings,
                                elements,
                            },
                        },
                    },
                    context: { fetchOptions: { signal: abortController.current.signal } },
                    onError: (e: any) => {
                        console.log(e);
                        enqueueSnackbar('Cannot update design setting.', { variant: 'error' });
                    },
                });
            }
        },
        1000,
        [designSettings, elements],
    );

    const handleClose = () => {
        setNodeId(null, true);
    };

    const [geoLocationQuery] = useGeoLocationLazyQuery();
    const { value: geoLocation } = useAsync(async () => {
        const geoData = await geoLocationQuery();
        if (geoData.data?.geoLocation) {
            return {
                lat: geoData.data.geoLocation.lat,
                lng: geoData.data.geoLocation.lng,
            };
        }
    }, []);

    return (
        <Fragment>
            {settingList.map((setting) => (
                <Dialog
                    key={setting.id}
                    anchor="bottom"
                    open={nodeId === setting.id}
                    title={setting.name}
                    PaperProps={{
                        style: {
                            height: ['Basic Info', 'Contact'].includes(setting.name as string) ? '75%' : 'auto',
                            paddingBottom: '30px',
                            maxWidth: 620,
                            margin: '0px auto',
                        },
                    }}
                    className={'hide-backdrop'}
                    onClose={handleClose}
                    showClose={true}
                    hideBackdrop
                >
                    <div className={`px-28 flex flex-col flex-auto overflow-y-auto`}>
                        {React.createElement(setting.settings, {
                            configs: setting.configs,
                            onClose: handleClose,
                            uploadImage,
                            upgradePlan: () => setModal(MODAL.UPGRADE_ACCOUNT),
                            geoLocation,
                        })}
                    </div>
                </Dialog>
            ))}
        </Fragment>
    );
};

export default ElementSettingDialog;
